import React, { createContext, useContext } from 'react';
import { useSessionTracking } from '@/hooks/useSessionTracking';
import { useEventTracking } from '@/hooks/useEventTracking';
import { useGA4PageTracking } from '@/hooks/useGA4PageTracking';

interface AnalyticsContextType {
  sessionId: string | null;
  trackEvent: ReturnType<typeof useEventTracking>['trackEvent'];
  trackButtonClick: ReturnType<typeof useEventTracking>['trackButtonClick'];
  trackFeatureUse: ReturnType<typeof useEventTracking>['trackFeatureUse'];
  trackFormSubmit: ReturnType<typeof useEventTracking>['trackFormSubmit'];
  trackNavigation: ReturnType<typeof useEventTracking>['trackNavigation'];
  trackAssessmentAction: ReturnType<typeof useEventTracking>['trackAssessmentAction'];
  trackExport: ReturnType<typeof useEventTracking>['trackExport'];
  trackProfileAction: ReturnType<typeof useEventTracking>['trackProfileAction'];
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

export const AnalyticsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { sessionId, incrementEventsTriggered } = useSessionTracking();
  const eventTracking = useEventTracking(sessionId);
  useGA4PageTracking();

  // Wrap trackEvent to also increment session events counter
  const trackEvent: AnalyticsContextType['trackEvent'] = async (params) => {
    await eventTracking.trackEvent(params);
    incrementEventsTriggered();
  };

  const value: AnalyticsContextType = {
    sessionId,
    trackEvent,
    trackButtonClick: eventTracking.trackButtonClick,
    trackFeatureUse: eventTracking.trackFeatureUse,
    trackFormSubmit: eventTracking.trackFormSubmit,
    trackNavigation: eventTracking.trackNavigation,
    trackAssessmentAction: eventTracking.trackAssessmentAction,
    trackExport: eventTracking.trackExport,
    trackProfileAction: eventTracking.trackProfileAction
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};
